import React from "react";
import ReactGA from "react-ga4";

import {
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import {
  PiChatCircleText,
  PiCopy,
  PiDotsThree,
  PiShareNetwork,
  PiWhatsappLogo,
} from "react-icons/pi";

const ShareOptions = ({ url, name, kind = "shop", fullWidth = false, buttonProps = {} }) => {
  const toast = useToast();
  const bg = useColorModeValue("#fdfaf5", "#241910");
  const border = useColorModeValue("rgba(0,0,0,0.10)", "rgba(255,255,255,0.08)");
  const text = useColorModeValue("#1a0c04", "#e8d9c4");
  const accent = useColorModeValue("#8b3e12", "#c8a97e");
  const hoverBg = useColorModeValue("rgba(139,62,18,0.07)", "rgba(200,169,126,0.08)");
  const iconBg = useColorModeValue("rgba(139,62,18,0.08)", "rgba(200,169,126,0.09)");
  const smsHref = `sms:?&body=${encodeURIComponent(url)}`;
  const encodedUrl = encodeURIComponent(url);
  const canUseSystemShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const shareLinks = [
    {
      label: "WhatsApp",
      icon: PiWhatsappLogo,
      href: `https://api.whatsapp.com/send?text=${encodedUrl}`,
    },
  ];

  const stopCardClick = (event) => event.stopPropagation();

  const trackShare = (event, platform) => {
    event.stopPropagation();
    ReactGA.event({
      category: "Social Share",
      action: "Share",
      label: `${platform} - ${name}`,
    });
  };

  const copyLink = async (event) => {
    trackShare(event, "Copy link");
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: `${kind[0].toUpperCase()}${kind.slice(1)} link copied`,
        status: "success",
        duration: 2200,
        isClosable: true,
        position: "bottom",
      });
    } catch {
      window.prompt(`Copy this ${kind} link:`, url);
    }
  };

  const systemShare = async (event) => {
    trackShare(event, "More options");
    try {
      await navigator.share({ url });
    } catch (error) {
      // Closing the operating-system share sheet is a normal user action.
      if (error?.name !== "AbortError") {
        toast({
          title: "Couldn't open sharing options",
          status: "error",
          duration: 2200,
          isClosable: true,
          position: "bottom",
        });
      }
    }
  };

  const icon = (Glyph) => (
    <Box
      w="28px"
      h="28px"
      borderRadius="full"
      bg={iconBg}
      color={accent}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Glyph aria-hidden style={{ width: "16px", height: "16px", display: "block" }} />
    </Box>
  );

  const itemProps = {
    bg: "transparent",
    color: text,
    borderRadius: "10px",
    p: 0,
    fontFamily: "body",
    fontSize: "13px",
    justifyContent: "flex-start",
    _hover: { bg: hoverBg, textDecoration: "none" },
    _focus: { bg: hoverBg },
  };

  const row = (Glyph, label) => (
    <HStack w="full" spacing={3} px={2} py="7px">
      {icon(Glyph)}
      <Text fontFamily="body" fontSize="13px" fontWeight={400} lineHeight="20px">
        {label}
      </Text>
    </HStack>
  );

  return (
    <Menu placement="auto" autoSelect={false}>
      {fullWidth ? (
        <MenuButton
          as={Button}
          {...buttonProps}
          onClick={stopCardClick}
        >
          <HStack justify="center" spacing="8px">
            <Box w="17px" h="17px" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
              <PiShareNetwork size="17px" aria-hidden />
            </Box>
            <Text as="span" lineHeight="1">Share</Text>
          </HStack>
        </MenuButton>
      ) : (
        <Tooltip label={`Share ${kind}`} hasArrow placement="top" openDelay={400}>
          <MenuButton
            as={IconButton}
            aria-label={`Share ${kind}`}
            icon={<PiShareNetwork size="16px" />}
            w="28px"
            h="28px"
            minW="28px"
            variant="ghost"
            borderRadius="full"
            onClick={stopCardClick}
          />
        </Tooltip>
      )}

      <Portal>
        <MenuList
          bg={bg}
          border="1px solid"
          borderColor={border}
          borderRadius="16px"
          boxShadow="0 14px 38px rgba(0,0,0,0.22)"
          p={2}
          minW="220px"
          overflow="hidden"
          zIndex="popover"
        >
          <Box px={2} pt={1} pb={2}>
            <Text fontFamily="heading" fontStyle="italic" fontSize="16px" color={text} noOfLines={1}>
              Share {name}
            </Text>
          </Box>
          <Divider borderColor={border} mb={1} />

          <MenuItem onClick={copyLink} {...itemProps}>
            {row(PiCopy, "Copy link")}
          </MenuItem>
          <MenuItem as={Link} href={smsHref} onClick={(event) => trackShare(event, "SMS")} {...itemProps}>
            {row(PiChatCircleText, "Messages")}
          </MenuItem>
          {shareLinks.map(({ label, icon: Glyph, href }) => (
            <MenuItem
              key={label}
              as={Link}
              href={href}
              isExternal
              onClick={(event) => trackShare(event, label)}
              {...itemProps}
            >
              {row(Glyph, label)}
            </MenuItem>
          ))}
          {canUseSystemShare && (
            <MenuItem onClick={systemShare} {...itemProps}>
              {row(PiDotsThree, "More options")}
            </MenuItem>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default ShareOptions;
